import { PROCESS } from "@/constants/testIds/landing";
import { PhoneCall, FileCheck, Sparkles, ArrowRight } from "lucide-react";
import Reveal from "@/components/site/Reveal";

const steps = [
  {
    num: "01",
    kicker: "Erstgespräch",
    title: "Kostenlose Beratung",
    desc: "Wir hören zu, prüfen deine Voraussetzungen und finden den passenden Kurs. Ohne Druck. Ohne Kosten.",
    icon: PhoneCall,
    time: "~ 20 Min · Telefon",
    img: "/images/cinema/hero-wide-backlight.jpg",
    pos: "center 40%",
  },
  {
    num: "02",
    kicker: "Förderung",
    title: "Bildungsgutschein",
    desc: "Wir helfen beim Antrag bei der Agentur für Arbeit oder dem Jobcenter — Schritt für Schritt.",
    icon: FileCheck,
    time: "~ 5–10 Tage · Antrag",
    img: "/images/cinema/scene-antrag.jpg",
    pos: "center 55%",
  },
  {
    num: "03",
    kicker: "Kursstart",
    title: "Start jeden Montag",
    desc: "Live-Online via Zoom, praxisnahe Inhalte, direkt im Pflegealltag umsetzbar.",
    icon: Sparkles,
    time: "Montags · Live-Online",
    img: "/images/cinema/scene-monday.jpg",
    pos: "center 40%",
  },
];

export default function Process() {
  return (
    <section className="relative bg-peach-50 py-28 md:py-40 overflow-hidden">
      {/* subtle editorial grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header — editorial, asymmetric */}
        <div className="grid md:grid-cols-12 gap-6 items-end mb-16 md:mb-20">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                So funktioniert&rsquo;s
              </span>
            </div>
            <h2 className="font-magazine font-medium text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] text-sand-800 leading-[1.08]">
              In drei Schritten <span className="text-terra">zur neuen Qualifikation.</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-sand-600 text-[15px] leading-[1.7] max-w-sm md:ml-auto md:text-right">
              Klarer Fahrplan, keine Warteschleifen — vom ersten Gespräch bis zum ersten Kurstag.
            </p>
          </div>
        </div>

        {/* Steps – editorial magazine columns */}
        <div className="grid md:grid-cols-3 gap-x-8 md:gap-x-12 gap-y-16 relative">
          {steps.map((s, idx) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.num} delay={idx * 140}>
                <div
                  data-testid={PROCESS.step}
                  className="group relative flex flex-col h-full"
                >
                  {/* Editorial-Foto */}
                  <div className="relative overflow-hidden rounded-sm mb-8 aspect-[16/10] bg-cream-200">
                    <img
                      src={s.img}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                      style={{ objectPosition: s.pos, filter: "saturate(0.95) contrast(1.04)" }}
                    />
                    <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-espresso-deep/45 to-transparent" />
                    <span className="photo-caption absolute bottom-3 left-4 font-magazine italic text-cream-50 text-[15px]">
                      {s.kicker}
                    </span>
                    <span className="absolute top-3 right-4 text-[10px] uppercase tracking-[0.28em] font-bold text-brand-rose">
                      {s.num}
                    </span>
                  </div>

                  {/* Icon medallion + editorial step meta */}
                  <div className="flex items-center gap-5 mb-8">
                    <div className="relative shrink-0">
                      <div className="h-16 w-16 rounded-full bg-brand-rose/25 flex items-center justify-center border border-brand-rose/40">
                        <Icon className="h-6 w-6 text-terra" strokeWidth={1.6} />
                      </div>
                      {/* soft outer ring */}
                      <div className="absolute inset-0 rounded-full ring-1 ring-terra/15 -m-2" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-terra font-semibold">
                        Schritt · {s.num}
                      </div>
                      <div className="font-magazine text-[13px] text-sand-700 italic mt-1">
                        {s.kicker}
                      </div>
                    </div>
                  </div>

                  {/* Editorial content block */}
                  <h3 className="font-magazine text-2xl lg:text-[1.75rem] text-sand-800 leading-[1.15]">
                    {s.title}
                  </h3>

                  <p className="mt-4 text-sand-600 text-[15px] leading-[1.7] max-w-sm">
                    {s.desc}
                  </p>

                  {/* Meta strip */}
                  <div className="mt-8 pt-5 border-t border-sand-300/60 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-[0.24em] text-sand-500 font-medium">
                      {s.time}
                    </span>
                    {idx === steps.length - 1 && (
                      <a
                        href="#kontakt"
                        data-testid={PROCESS.cta}
                        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-terra hover:text-brand-red transition-colors"
                      >
                        Jetzt starten
                        <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                      </a>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}

          {/* Editorial connector dots between columns (desktop only) */}
          <div
            aria-hidden="true"
            className="hidden md:block absolute left-0 right-0"
            style={{ top: "2rem" }}
          >
            <div className="max-w-7xl mx-auto grid grid-cols-3 gap-x-12 items-center pointer-events-none">
              <div className="flex justify-end pr-6">
                <span className="h-px w-16 bg-gradient-to-r from-transparent to-terra/40" />
              </div>
              <div className="flex justify-center px-4">
                <span className="h-px w-full bg-gradient-to-r from-terra/40 via-terra/20 to-terra/40" />
              </div>
              <div className="flex justify-start pl-6">
                <span className="h-px w-16 bg-gradient-to-l from-transparent to-terra/40" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
